import { NaluriChatError } from '@/errors';
import type { AccountUser, TokenPayload } from '@/types/account';
import { invariant } from '@/utils/invariant';
import { tokenStore } from '@/utils/tokenStore';
import version from '@/utils/version';

const API_KEY_HEADER = 'x-api-key';

export interface AccountOptions {
  apiKey: string;
  apiUrl: string;
  timeoutMs?: number;
  onRefreshTokenError?: () => void | Promise<void>;
}

export class AccountApi {
  private userId: string;
  private userReference: string;
  private apiKey: string;
  private apiUrl: string;

  constructor(opts: AccountOptions) {
    Object.assign(this, opts);
  }

  public async login(authorizationCode: string) {
    invariant(authorizationCode, 'authorization code is required.');

    const response = await fetch(`${this.apiUrl}/auth/login`, {
      method: 'POST',
      body: JSON.stringify({
        authorizationCode,
      }),
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new NaluriChatError({
        code: '4003',
        message: 'Failed to login',
      });
    }

    const data = (await response.json()) as TokenPayload & {
      user: AccountUser;
      reference: string;
    };

    tokenStore.setItem('accessToken', data.accessToken);
    tokenStore.setItem('refreshToken', data.refreshToken);

    this.userId = data.user.id;
    this.userReference = data.reference;

    return data;
  }

  public async refresh(refreshToken: string) {
    invariant(refreshToken, 'refresh token is required.');

    const response = await fetch(`${this.apiUrl}/auth/refresh`, {
      method: 'POST',
      body: JSON.stringify({
        refreshToken: tokenStore.getItem('refreshToken'),
      }),
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new NaluriChatError({
        code: '4011',
        message: 'Failed to refresh token',
      });
    }

    const data = (await response.json()) as TokenPayload;

    return data;
  }

  public async logout(refreshToken: string) {
    invariant(refreshToken, 'refresh token is required.');

    const response = await fetch(`${this.apiUrl}/auth/logout`, {
      method: 'POST',
      body: JSON.stringify({
        refreshToken,
      }),
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new NaluriChatError({
        code: '4004',
        message: 'Failed to logout',
      });
    }

    const data = (await response.json()) as TokenPayload;

    return data;
  }

  public getUserId() {
    return this.userId;
  }

  public getUserReference() {
    return this.userReference;
  }

  public getApiUrl() {
    return this.apiUrl;
  }

  public getApiKey() {
    return this.apiKey;
  }

  protected getHeaders() {
    return {
      [API_KEY_HEADER]: this.apiKey,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'User-Agent': `Naluri Chat SDK /JS (${version}})`,
      'x-nl-sdk-version': version,
    };
  }
}
